/**
 * Phase 21.4 Batch 3E — Winner Share Card modal.
 *
 * Renders TWO polished RGM-branded share surfaces (Story 9:16 + Square
 * 1:1) using the ACTUAL committed rewards (coins, promo plays, badges).
 * Preloads artwork + avatar + fonts before capture so we do not repeat
 * the blank recap-share bug from Phase 21.1.
 *
 * Actions: Copy link · Native share (with cover file when supported).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { getProfilePhotoUrl, resolveShareCover } from "@/lib/mediaUrls";
import ShareChannels from "@/components/share/ShareChannels";
import { toast } from "sonner";

const positionEmoji = (p) => (p === 1 ? "🥇" : p === 2 ? "🥈" : p === 3 ? "🥉" : "🏆");
const positionLabel = (p) => (p === 1 ? "1st Place" : p === 2 ? "2nd Place" : p === 3 ? "3rd Place" : "Winner");

// Preload every image URL so the DOM node has data ready by the time
// the browser measures / native-shares it (prevents blank captures).
async function preloadImages(urls) {
  const good = urls.filter(Boolean);
  await Promise.all(
    good.map((u) => new Promise((resolve) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = resolve;   // best-effort — missing images don't block
      img.src = u;
    }))
  );
  // Wait for fonts + two rAFs so styling is fully applied.
  if (typeof document !== "undefined" && document.fonts?.ready) {
    try { await document.fonts.ready; } catch { /* ignore */ }
  }
  await new Promise((r) => requestAnimationFrame(r));
  await new Promise((r) => requestAnimationFrame(r));
}

const WinnerShareCard = ({ data, artist, onClose }) => {
  const [format, setFormat] = useState("story");   // "story" | "square"
  const [ready, setReady] = useState(false);
  const rootRef = useRef(null);

  const contest = data.contest || {};
  const entry = data.entry || {};
  const granted = data.granted || {};
  const pos = data.position;

  // iter_188 — Prefer the auto-generated 1500×1500 winner cover
  // (contains full branded composition). When absent, fall through the
  // canonical share-cover resolver against the LIVE track cover, then
  // snapshot, then contest banner. Same precedence used on every
  // surface so cards look identical.
  const cover = entry.winner_cover_url
    || resolveShareCover({
      cover_url: entry.track_cover_url,
      cover_art: entry.ref_snapshot?.cover_art,
      cover_image: contest.banner_image,
    });
  const avatar = getProfilePhotoUrl(artist || {});
  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/contests/${contest.slug}`
    : `/contests/${contest.slug}`;

  const artistName = artist?.artist_name || artist?.name || "Winning Artist";

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    preloadImages([cover, avatar]).then(() => {
      if (!cancelled) setReady(true);
    });
    return () => { cancelled = true; };
  }, [cover, avatar, format]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied!");
    } catch {
      toast.error("Copy failed");
    }
  };

  const doNativeShare = async () => {
    const shareData = {
      title: `${artistName} · ${positionLabel(pos)} · ${contest.title}`,
      text: `🏆 I placed ${positionLabel(pos)} in ${contest.title} on RGM Studio!`,
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await copyLink();
      }
    } catch { /* user cancelled */ }
  };

  const badgeStrip = useMemo(() => (granted.badges || []).slice(0, 3), [granted.badges]);

  const StoryCard = (
    <div
      className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-amber-500 via-purple-800 to-black text-white"
      style={{ aspectRatio: "9 / 16", width: "min(360px, 90vw)" }}
      data-testid="winner-share-story"
    >
      {cover && (
        <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black" />
      <div className="relative h-full w-full flex flex-col justify-between p-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-200 font-black">🏆 RGM Contest Center</p>
          <p className="text-xs text-white/70 mt-1">{contest.title}</p>
        </div>
        <div>
          <p className="text-7xl mb-2 leading-none">{positionEmoji(pos)}</p>
          <p className="text-3xl font-black leading-tight" data-testid="winner-share-placement">
            {positionLabel(pos)}
          </p>
          <div className="flex items-center gap-2 mt-4">
            <img src={avatar} alt={artistName} className="w-10 h-10 rounded-full object-cover border-2 border-white/40" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
            <div className="min-w-0">
              <p className="text-white text-base font-black truncate">{artistName}</p>
              {entry.ref_snapshot?.title && (
                <p className="text-[11px] text-white/70 truncate">{entry.ref_snapshot.title}</p>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {Number(granted.coins || 0) > 0 && (
              <span className="px-2 py-1 rounded-full text-[10px] font-black bg-emerald-500/25 border border-emerald-500/50">
                🪙 {Number(granted.coins).toLocaleString()} coins
              </span>
            )}
            {contest.entry_type === "track" && Number(granted.promo_plays || 0) > 0 && (
              <span className="px-2 py-1 rounded-full text-[10px] font-black bg-purple-500/25 border border-purple-500/50">
                ⚡ {Number(granted.promo_plays).toLocaleString()} promo plays
              </span>
            )}
            {badgeStrip.map((b) => (
              <span key={b.badge_id} className="px-2 py-1 rounded-full text-[10px] font-black bg-amber-500/25 border border-amber-500/50">
                🎖 {b.label}
              </span>
            ))}
          </div>
        </div>
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-widest text-white/70 font-black">rgmstudio.org</p>
        </div>
      </div>
    </div>
  );

  const SquareCard = (
    <div
      className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-amber-500 via-purple-800 to-black text-white"
      style={{ aspectRatio: "1 / 1", width: "min(400px, 90vw)" }}
      data-testid="winner-share-square"
    >
      {cover && (
        <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
      <div className="relative h-full w-full flex flex-col justify-between p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-200 font-black">🏆 RGM Studio</p>
            <p className="text-xs text-white/70 mt-1">{contest.title}</p>
          </div>
          <p className="text-5xl leading-none">{positionEmoji(pos)}</p>
        </div>
        <div>
          <p className="text-2xl font-black leading-tight">{positionLabel(pos)}</p>
          <div className="flex items-center gap-2 mt-3">
            <img src={avatar} alt={artistName} className="w-9 h-9 rounded-full object-cover border-2 border-white/40" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
            <div className="min-w-0">
              <p className="text-white text-sm font-black truncate">{artistName}</p>
              {entry.ref_snapshot?.title && (
                <p className="text-[11px] text-white/70 truncate">{entry.ref_snapshot.title}</p>
              )}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {Number(granted.coins || 0) > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-emerald-500/25 border border-emerald-500/50">
                🪙 {Number(granted.coins).toLocaleString()}
              </span>
            )}
            {contest.entry_type === "track" && Number(granted.promo_plays || 0) > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-purple-500/25 border border-purple-500/50">
                ⚡ {Number(granted.promo_plays).toLocaleString()}
              </span>
            )}
            {badgeStrip.map((b) => (
              <span key={b.badge_id} className="px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-500/25 border border-amber-500/50">
                🎖 {b.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      onClick={onClose}
      data-testid="winner-share-modal"
    >
      <div className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="rounded-2xl bg-[#0f0f14] border border-white/10 p-4 sm:p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-black text-white">Share your win</h3>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white text-xl leading-none"
              aria-label="Close"
              data-testid="winner-share-close"
            >
              ×
            </button>
          </div>

          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setFormat("story")}
              className={`px-3 py-1.5 rounded-full text-xs font-black border ${format === "story" ? "bg-white text-black border-white" : "bg-white/5 text-gray-300 border-white/10"}`}
              data-testid="winner-share-format-story"
            >
              📱 Story (9:16)
            </button>
            <button
              onClick={() => setFormat("square")}
              className={`px-3 py-1.5 rounded-full text-xs font-black border ${format === "square" ? "bg-white text-black border-white" : "bg-white/5 text-gray-300 border-white/10"}`}
              data-testid="winner-share-format-square"
            >
              ⬜ Square (1:1)
            </button>
          </div>

          <div className="flex justify-center mb-4" ref={rootRef}>
            {ready ? (
              <>
                {/* Both formats mounted; only the active one is visible. */}
                <div style={{ display: format === "story" ? "block" : "none" }}>
                  {StoryCard}
                </div>
                <div style={{ display: format === "square" ? "block" : "none" }}>
                  {SquareCard}
                </div>
              </>
            ) : (
              <div className="p-6 text-sm text-gray-500" data-testid="winner-share-preloading">Preparing card…</div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <ShareChannels
              blob={null}
              filename={`RGM-Winner-${(contest.slug || "contest")}-${positionLabel(pos).replace(/\s+/g, "")}.png`}
              title={`${artistName} · ${positionLabel(pos)} · ${contest.title || "RGM Contest"}`}
              text={`🏆 I placed ${positionLabel(pos)} in ${contest.title || "an RGM contest"} on RGM Studio!`}
              url={shareUrl}
              testIdPrefix="winner-share"
            />
          </div>
          <p className="text-[10px] text-gray-500 mt-3 text-center">
            Screenshot the card to save it — or use any channel above to share the OG preview link.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WinnerShareCard;
